import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import './Scenario.css'
import { SyncStatusIndicator } from '../components/SyncStatusIndicator'
import { AccessDeniedBanner } from '../components/AccessDeniedBanner'
import { useScenario } from '../hooks/useScenario'
import { useCommand } from '../hooks/useCommand'
import { ShareModal } from '../components/ShareModal'
import { EmptyState } from '../components/EmptyState'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { DropdownMenu } from '../components/DropdownMenu'
import type { MenuItem } from '../components/DropdownMenu'
import { FormModal } from '../components/FormModal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal'
import { FlowIcon } from '../components/FlowIcon'
import { FormField, TextInput } from '../components/FormField'
import type { ListForUI } from '../../core/queries/projections'
import { ScenarioKindIcon, ListKindIcon, ItemsKindIcon } from '../components/PageKindIcons'
import { useSmartBack } from '../hooks/useSmartBack'
import { suggestScenarioDatedName } from '../utils/suggestedNames'

function Scenario() {
  const { scenarioId } = useParams<{ scenarioId: string }>()
  const navigate = useNavigate()

  const smartBackToHome = useSmartBack('/')

  const { user, userDetails, switchAccount } = useAuth()
  
  // Use hooks for data and commands
  // Use passive mode because ScenarioLayout handles the hydration/subscription
  const { scenario, loading: scenarioLoading, accessDenied, dismissAccessDenied, deleted } = useScenario(scenarioId!, { passive: true })
  const { 
    createList: createListCmd, 
    renameScenario, 
    deleteScenario: deleteScenarioCmd, 
    leaveScenario: leaveScenarioCmd,
    rollOverList 
  } = useCommand()
  
  // Extract lists from scenario (nested property)
  const lists = scenario?.lists || []
  
  // Detect if scenario is in "Bootstrap Mode" (sequence -1)
  const isBootstrapping = scenario?.sequenceNumber === -1
  
  // Consolidate loading logic:
  // If useScenario says loading, OR specifically scenario is null but we haven't determined access denied yet (and not bootstrapping)
  // Wait, useScenario's loading flag should be the source of truth.
  // BUT, we want to make sure we don't flash "Loading" -> "Error" -> "Scenario"
  const loading = scenarioLoading;

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [rolloverList, setRolloverList] = useState<ListForUI | null>(null)
  const [rolloverName, setRolloverName] = useState('')
  const [newListName, setNewListName] = useState('')
  const [newListDescription, setNewListDescription] = useState('')
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [rolloverLoading, setRolloverLoading] = useState(false)
  const [rolloverError, setRolloverError] = useState<string | null>(null)

  const openCreateListModal = () => {
    // Only suggest when the field is currently empty, so we don't overwrite user input.
    setNewListName((current) => (current.trim() ? current : suggestScenarioDatedName(scenario?.name)))
    setShowCreateModal(true)
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCreateModal) setShowCreateModal(false);
        if (showEditModal) setShowEditModal(false);
        if (showShareModal) setShowShareModal(false);
        if (showMenu) setShowMenu(false);
        if (showDeleteModal && !isDeleting) setShowDeleteModal(false);
        if (showLeaveModal && !leaving) setShowLeaveModal(false);
        if (rolloverList && !rolloverLoading) setRolloverList(null);
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showCreateModal, showEditModal, showShareModal, showMenu, showDeleteModal, showLeaveModal, rolloverList, isDeleting, leaving, rolloverLoading]);

  useEffect(() => {
    if (deleted) {
      navigate('/', { replace: true })
    }
  }, [deleted, navigate])

  const handleLeaveScenario = async () => {
    if (!scenario || !scenarioId) return
setLeaving(true)
    try {
      await leaveScenarioCmd(scenarioId)
      navigate('/')
    } catch (error) {
      console.error('Error leaving scenario:', error)
      alert('Failed to leave scenario. Please try again.')
    } finally {
      setLeaving(false)
      setShowLeaveModal(false)
    }
    setShowLeaveModal(false)
  }

  const createList = async () => {
    if (!newListName.trim()) return

    try {
      const listId = `lst_${crypto.randomUUID()}`
      
      await createListCmd(
        scenarioId!,
        listId,
        newListName.trim(),
        newListDescription.trim() || ''
      )
      
      setNewListName('')
      setNewListDescription('')
      setShowCreateModal(false)
      
      // Navigate to the newly created list
      navigate(`/scenario/${scenarioId}/list/${listId}`)
    } catch (error) {
      console.error('Error creating list:', error)
    }
  }

  const updateScenario = async () => {
    if (!editName.trim() || !scenarioId) return

    try {
      await renameScenario(
        scenarioId,
        editName.trim(),
        editDescription.trim() || undefined,
        scenario?.name // Pass old name for metadata
      )
      
      setShowEditModal(false)
    } catch (error) {
      console.error('Error updating scenario:', error)
    }
  }

  const handleDeleteScenario = async () => {
    if (!scenarioId) return
    
    setIsDeleting(true)
    try {
      await deleteScenarioCmd(scenarioId, scenario?.name)
      
      // Navigate back to home
      navigate('/')
    } catch (error) {
      console.error('Error deleting scenario:', error)
      alert('Failed to delete scenario. Please try again.')
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  // Calculate list completion based on items
  const activeLists = lists.filter((list) => {
    const totalItems = list.itemsSorted.length
    if (totalItems === 0) return true; // Empty lists are always active
    const completedItems = list.itemsSorted.filter(item => item.isCompleted).length
    return completedItems < totalItems; // Not all items completed
  });

  const completedLists = lists.filter((list) => {
    const totalItems = list.itemsSorted.length
    if (totalItems === 0) return false; // Empty lists are not completed
    const completedItems = list.itemsSorted.filter(item => item.isCompleted).length
    return completedItems === totalItems && totalItems > 0; // All items completed, not empty
  });

  if (loading) {
    return <LoadingSpinner message="Loading scenario..." fullPage />
  }

  if (!scenario) {
    const handleLogin = () => {
      const returnUrl = window.location.pathname + window.location.search
      // replace() removes the current URL from history so pressing back after
      // the OAuth flow does not land on the Google sign-in page.
      window.location.replace(
        `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(returnUrl)}`
      )
    }

    const handleSwitchAccount = switchAccount

    const actions = user ? [
      { label: 'Back to Scenarios', onClick: () => navigate('/'), variant: 'primary' as const },
      { label: 'Switch Account', onClick: handleSwitchAccount, variant: 'secondary' as const }
    ] : [
      { label: 'Sign in with Google', onClick: handleLogin, variant: 'primary' as const },
      { label: 'Back to Scenarios', onClick: () => navigate('/'), variant: 'secondary' as const }
    ]

    return (
      <div className="app-container">
        <header className="app-header">
          <div className="header-content">
             <div style={{ fontWeight: 600, fontSize: '1.25rem' }}>Ordelio</div>
          </div>
        </header>

        <PwaUpdateBanner />

        <EmptyState
          icon="🔒"
          title={user ? 'Scenario Not Available' : 'Sign In Required'}
          description={user 
            ? "This scenario might have been deleted, or you don't have permission to view it with your current account." 
            : "You need to be signed in to view and interact with this scenario."
          }
          context={user && (
            <>Signed in as <strong style={{ color: 'white' }}>{userDetails?.email || user.userDetails || 'unknown user'}</strong></>
          )}
          actions={actions}
        />
      </div>
    )
  }

  // Handler for Roll Over
  const handleRollover = (list: any) => {
    setRolloverList(list)
    setRolloverError(null)
    setRolloverName(list.name || '')
  }

  const confirmRollover = async () => {
    if (!rolloverList) return
    
    setRolloverLoading(true)
    setRolloverError(null)
    
    try {
      // Call new command-based rollover
      const result = await rollOverList(scenarioId!, rolloverList.id, rolloverName)
      
      // Close modal and navigate to new list
      setRolloverList(null)
      navigate(`/scenario/${scenarioId}/list/${result.newListId}`)
    } catch (err: any) {
      // Show error in modal
      setRolloverError(err?.message || 'Failed to roll over list')
    } finally {
      setRolloverLoading(false)
    }
  }

  const cancelRollover = () => {
    setRolloverList(null)
    setRolloverError(null)
  }

  const menuItems: MenuItem[] = [
    {
      icon: '✎',
      label: 'Edit Scenario',
      onClick: () => {
        setEditName(scenario?.name || '');
        setEditDescription(scenario?.description || '');
        setShowEditModal(true);
      }
    },
    {
      icon: '👥',
      label: 'Share Scenario',
      onClick: () => setShowShareModal(true)
    },
    {
      icon: <FlowIcon size={16} />,
      label: 'Flows',
      onClick: () => navigate(`/scenario/${scenarioId}/flows`)
    },
    {
      icon: <ItemsKindIcon width={16} height={16} />,
      label: 'All Items',
      onClick: () => navigate(`/scenario/${scenarioId}/items`)
    },
    {
      divider: true,
      icon: scenario.role === 'owner' ? '🗑' : '🚪',
      label: scenario.role === 'owner' ? 'Delete Scenario' : 'Leave Scenario',
      onClick: () => scenario.role === 'owner' ? setShowDeleteModal(true) : setShowLeaveModal(true),
      variant: 'danger'
    }
  ];

  return (
    <div className="app-container">
      <PageHeader
        title={scenario.name}
        kind="Scenario"
        kindIcon={<ScenarioKindIcon />}
        tone="scenario"
        backLabel="Scenarios"
        onBack={smartBackToHome}
        badge={isBootstrapping ? '☁ Loading…' : undefined}
        actions={
          <>
            <SyncStatusIndicator />
            <div className="menu-container">
              <button 
                onClick={() => setShowMenu(!showMenu)} 
                className="menu-btn" 
                title="Scenario options"
              >
                ⋮
              </button>
              <DropdownMenu 
                isOpen={showMenu} 
                onClose={() => setShowMenu(false)}
                items={menuItems}
              />
            </div>
          </>
        }
      />

      <PwaUpdateBanner />

      <main className="app-main">
        <div className="scenario-content">
          {accessDenied && (
            <AccessDeniedBanner onDismiss={dismissAccessDenied} />
          )}
          
          {scenario.description && (
            <div className="scenario-description">
              <p>{scenario.description}</p>
            </div>
          )}

          <div className="lists-section">
            <div className="lists-header">
              <h2>Active Lists</h2>
              <div className="header-actions">
                <button onClick={() => navigate(`/scenario/${scenarioId}/list-builder`)} className="create-btn secondary">
                  Build from Items
                </button>
                <button onClick={openCreateListModal} className="create-btn">
                  Create New List
                </button>
              </div>
            </div>

            <div className="lists-grid">
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading lists...</p>
                </div>
              ) : activeLists.length > 0 ? (
                activeLists.map((list) => {
                  const totalItems = list.itemsSorted.length
                  const completedItems = list.itemsSorted.filter(item => item.isCompleted).length
                  const uncheckedItems = list.itemsSorted.filter(item => !item.isCompleted)
                  return (
                    <div 
                      key={list.id} 
                      className="list-tile clickable"
                      onClick={() => navigate(`/scenario/${scenarioId}/list/${list.id}`)}
                    >
                      <div className="list-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                          <ListKindIcon style={{ minWidth: 20, width: 20, height: 20, opacity: 0.7 }} />
                          <h3>{list.name}</h3>
                        </div>
                        {uncheckedItems.length > 0 && (
                          <button
                            className="secondary-btn"
                            style={{ marginLeft: 8 }}
                            title="Roll over incomplete items to a new list"
                            onClick={e => { e.stopPropagation(); handleRollover(list); }}
                          >
                            Roll Over
                          </button>
                        )}
                      </div>
                      {list.description && <p>{list.description}</p>}
                      <div className="list-meta">
                        {totalItems > 0 && (
                          <span className="item-count">✓ {completedItems}/{totalItems}</span>
                        )}
                        <span>Created {new Date(list.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="no-lists">
                  <p>No active lists. Create your first list to get started!</p>
                </div>
              )}
            </div>
                  {rolloverList && (
                    <Modal
                      isOpen={true}
                      onClose={cancelRollover}
                      preventCloseOnOverlay={rolloverLoading}
                      size="medium"
                    >
                      <ModalHeader
                        title="Roll Over Incomplete Items"
                        onClose={cancelRollover}
                      />
                      <ModalBody>
                        <FormField label="New List Name" htmlFor="rollover-name" required>
                          <TextInput
                            id="rollover-name"
                            value={rolloverName}
                            onChange={setRolloverName}
                            autoFocus
                          />
                        </FormField>
                        <p>The following incomplete items will be carried over to a new list and removed from this one:</p>
                        <ul style={{ margin: '1rem 0', paddingLeft: 20 }}>
                          {rolloverList.itemsSorted.filter(item => !item.isCompleted).map((item) => (
                            <li key={item.id}>{item.name}</li>
                          ))}
                        </ul>
                        {rolloverError && <div style={{ color: '#e74c3c', marginBottom: 8 }}>{rolloverError}</div>}
                      </ModalBody>
                      <ModalFooter>
                        <button onClick={cancelRollover} className="cancel-btn" disabled={rolloverLoading}>Cancel</button>
                        <button onClick={confirmRollover} className="submit-btn" disabled={rolloverLoading || !rolloverName.trim()}>
                          {rolloverLoading ? 'Rolling Over...' : 'Confirm Roll Over'}
                        </button>
                      </ModalFooter>
                    </Modal>
                  )}
          </div>

          {completedLists.length > 0 && (
            <div className="completed-lists-section">
              <h2>Completed Lists</h2>
              <div className="lists-grid">
                {completedLists.map((list) => {
                  const totalItems = list.itemsSorted.length
                  const completedItems = list.itemsSorted.filter(item => item.isCompleted).length
                  
                  return (
                    <div 
                      key={list.id} 
                      className="list-tile completed clickable"
                      onClick={() => navigate(`/scenario/${scenarioId}/list/${list.id}`)}
                    >
                      <div className="list-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                          <ListKindIcon style={{ minWidth: 20, width: 20, height: 20, opacity: 0.7 }} />
                          <h3>{list.name}</h3>
                        </div>
                        <span className="completed-badge">✓ Completed</span>
                      </div>
                      {list.description && <p>{list.description}</p>}
                      <div className="list-meta">
                        {totalItems > 0 && (
                          <span className="item-count">✓ {completedItems}/{totalItems}</span>
                        )}
                        <span>Created {new Date(list.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <FormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={createList}
          title="Create New List"
          submitLabel="Create List"
          name={newListName}
          onNameChange={setNewListName}
          nameLabel="List Name"
          namePlaceholder="Enter list name"
          description={newListDescription}
          onDescriptionChange={setNewListDescription}
          descriptionLabel="Description"
          descriptionPlaceholder="Describe your list (optional)"
        />

        <FormModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={updateScenario}
          title="Edit Scenario"
          submitLabel="Save Changes"
          name={editName}
          onNameChange={setEditName}
          nameLabel="Scenario Name"
          namePlaceholder="Enter scenario name"
          description={editDescription}
          onDescriptionChange={setEditDescription}
          descriptionLabel="Description"
          descriptionPlaceholder="Describe your scenario (optional)"
        />

        {showShareModal && scenario && (
          <ShareModal
            scenarioId={scenario.id}
            scenarioName={scenario.name}
            canManageInvites={scenario.role === 'owner'}
            onClose={() => setShowShareModal(false)}
          />
        )}

        <ConfirmDialog
          isOpen={showLeaveModal}
          onClose={() => setShowLeaveModal(false)}
          onConfirm={handleLeaveScenario}
          title="Leave Scenario"
          message={`Are you sure you want to leave "${scenario.name}"?`}
          warningMessage="You will no longer have access to this scenario and its lists. You can only rejoin if the owner invites you again."
          confirmLabel="Leave Scenario"
          confirmVariant="danger"
          isProcessing={leaving}
          processingLabel="Leaving..."
        />

        <ConfirmDialog
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteScenario}
          title="Delete Scenario"
          message={`Are you sure you want to delete "${scenario.name}"?`}
          warningMessage="This will permanently delete the scenario and all its lists and items. This action cannot be undone."
          confirmLabel="Delete Scenario"
          confirmVariant="danger"
          isProcessing={isDeleting}
          processingLabel="Deleting..."
        />
      </main>
    </div>
  )
}

export default Scenario
